// Modules
import { useEffect, useState, useCallback, useRef } from 'react';

// Styles
import './joystick.dish.styles.scss';

// Properties
import { useTranslation } from 'react-i18next';
import { JoystickDishProperties } from './joystick.dish.properties';

/**
 * Joystick Dish Component
 * @description Component to control a camera
 * @param {JoystickDishProperties} properties
 * @returns {JSX.Element}
 */
export const JoystickDishComponent = (props: JoystickDishProperties): JSX.Element | null => {
    // zoom control
    const [maxZoom, setMaxZoom] = useState<number>(0);
    const [minZoom, setMinZoom] = useState<number>(0);
    const [zoom, setZoom] = useState<number>(0);
    const [zoomStatus, setZoomStatus] = useState<boolean>(false);

    // pan control
    const [maxPan, setMaxPan] = useState<number>(0);
    const [minPan, setMinPan] = useState<number>(0);
    const [pan, setPan] = useState<number>(0);
    const [panStatus, setPanStatus] = useState<boolean>(false);

    // tilt control
    const [maxTilt, setMaxTilt] = useState<number>(0);
    const [minTilt, setMinTilt] = useState<number>(0);
    const [tilt, setTilt] = useState<number>(0);
    const [tiltStatus, setTiltStatus] = useState<boolean>(false);

    // status
    const [status, setStatus] = useState<boolean>(false);
    const [isDeviceRegistered, setIsDeviceRegistered] = useState<boolean>(false);

    // Track previous PTZ capabilities to detect when they disappear
    const [hadPtzCapabilities, setHadPtzCapabilities] = useState<boolean>(false);

    const { t } = useTranslation();

    useEffect(() => {
        // Para streams remotos, usar la información de PTZDeviceInfo
        if (props.isRemoteStream && props.ptzDeviceInfo) {
            const device = props.ptzDeviceInfo;

            // Configurar capacidades desde la información del dispositivo
            if (device.capabilities.zoom) {
                setZoomStatus(true);
                setMaxZoom(device.capabilities.zoom.max);
                setMinZoom(device.capabilities.zoom.min);
                setZoom(device.capabilities.zoom.current);
            }

            if (device.capabilities.pan) {
                setPanStatus(true);
                setMaxPan(device.capabilities.pan.max);
                setMinPan(device.capabilities.pan.min);
                setPan(device.capabilities.pan.current);
            }

            if (device.capabilities.tilt) {
                setTiltStatus(true);
                setMaxTilt(device.capabilities.tilt.max);
                setMinTilt(device.capabilities.tilt.min);
                setTilt(device.capabilities.tilt.current);
            }
        } else {
            // Para streams locales, usar la lógica existente
            const [track] = props.stream.getVideoTracks();

            if (track && track.getCapabilities) {

                // get capabilities of video track
                const capabilities = track.getCapabilities();

                // get settings of video track
                const settings = track.getSettings();

                if (capabilities && settings) {

                    // @ts-ignore
                    const hasZoom = capabilities?.zoom ? true : false;
                    // @ts-ignore
                    const hasPan = capabilities?.pan ? true : false;
                    // @ts-ignore
                    const hasTilt = capabilities?.tilt ? true : false;

                    const currentlyHasPtz = hasZoom || hasPan || hasTilt;

                    // Check if PTZ capabilities disappeared
                    if (hadPtzCapabilities && !currentlyHasPtz && isDeviceRegistered && props.socketInstance) {
                        console.log(`PTZ capabilities lost for device ${props.stream.id}, sending disconnect`);

                        // Send disconnect event
                        props.socketInstance.emit('ptz:disconnect', {
                            deviceId: props.stream.id,
                        });

                        setIsDeviceRegistered(false);
                        setHadPtzCapabilities(false);
                    }

                    setZoomStatus(hasZoom);
                    setPanStatus(hasPan);
                    setTiltStatus(hasTilt);

                    const ptzPresets = localStorage.getItem(`ptz-presets`);
                    const ptzZoomPreset = ptzPresets ? JSON.parse(ptzPresets)[0].position.zoom : null;
                    const ptzPanPreset = ptzPresets ? JSON.parse(ptzPresets)[0].position.pan : null;
                    const ptzTiltPreset = ptzPresets ? JSON.parse(ptzPresets)[0].position.tilt : null;

                    // @ts-ignore
                    setMaxZoom(capabilities?.zoom?.max);
                    // @ts-ignore
                    setMinZoom(capabilities?.zoom?.min);
                    // @ts-ignore
                    setMaxPan(capabilities?.pan?.max);
                    // @ts-ignore
                    setMinPan(capabilities?.pan?.min);
                    // @ts-ignore
                    setMaxTilt(capabilities?.tilt?.max);
                    // @ts-ignore
                    setMinTilt(capabilities?.tilt?.min);
                    // @ts-ignore
                    setPan(ptzPanPreset || settings?.pan);
                    // @ts-ignore
                    setZoom(ptzZoomPreset || settings?.zoom);
                    // @ts-ignore
                    setTilt(ptzTiltPreset || settings?.tilt);

                    // Register PTZ device if it has any PTZ capabilities and it's not a remote stream
                    if (currentlyHasPtz && !props.isRemoteStream && !isDeviceRegistered && props.socketInstance) {
                        // Simple registration without localStorage management
                        const ptzCapabilities = {
                            // @ts-ignore - PTZ capabilities are not in standard MediaTrackCapabilities type
                            pan: capabilities?.pan ? {
                                // @ts-ignore
                                min: capabilities.pan.min || 0,
                                // @ts-ignore
                                max: capabilities.pan.max || 0,
                                // @ts-ignore
                                current: settings?.pan || 0,
                                supported: true,
                                // @ts-ignore
                                step: capabilities.pan.step || 1
                            } : false,
                            // @ts-ignore - PTZ capabilities are not in standard MediaTrackCapabilities type
                            tilt: capabilities?.tilt ? {
                                // @ts-ignore
                                min: capabilities.tilt.min || 0,
                                // @ts-ignore
                                max: capabilities.tilt.max || 0,
                                // @ts-ignore
                                current: settings?.tilt || 0,
                                supported: true,
                                // @ts-ignore
                                step: capabilities.tilt.step || 1
                            } : false,
                            // @ts-ignore - PTZ capabilities are not in standard MediaTrackCapabilities type
                            zoom: capabilities?.zoom ? {
                                // @ts-ignore
                                min: capabilities.zoom.min || 0,
                                // @ts-ignore
                                max: capabilities.zoom.max || 0,
                                // @ts-ignore
                                current: settings?.zoom || 0,
                                supported: true,
                                // @ts-ignore
                                step: capabilities.zoom.step || 1
                            } : false,
                            presets: [{
                                id: 'initial',
                                name: 'Estado Inicial',
                                position: {
                                    // @ts-ignore
                                    pan: settings?.pan || 0,
                                    // @ts-ignore
                                    tilt: settings?.tilt || 0,
                                    // @ts-ignore
                                    zoom: settings?.zoom || 0
                                }
                            }]
                        };

                        props.socketInstance.emit('ptz:register:device', {
                            deviceId: props.stream.id,
                            capabilities: ptzCapabilities
                        });

                        if (props.onPtzDeviceRegistered) {
                            props.onPtzDeviceRegistered(props.stream.id, ptzCapabilities);
                        }

                        // If presets, save the initial preset
                        if (ptzCapabilities.presets && ptzCapabilities.presets.length > 0) {
                            props.savePtzPreset?.(props.stream.id, ptzCapabilities.presets[0].position);
                        }

                        setIsDeviceRegistered(true);
                        setHadPtzCapabilities(true);
                    } else if (currentlyHasPtz) {
                        // Update hadPtzCapabilities even if already registered
                        setHadPtzCapabilities(true);
                    } else if (!currentlyHasPtz) {
                        // No PTZ capabilities
                        setHadPtzCapabilities(false);
                    }

                } else {
                    // No capabilities or settings available
                    if (hadPtzCapabilities && isDeviceRegistered && props.socketInstance) {
                        console.log(`PTZ capabilities lost (no capabilities/settings) for device ${props.stream.id}, sending disconnect`);

                        props.socketInstance.emit('ptz:disconnect', {
                            deviceId: props.stream.id,
                            reason: 'manual',
                            ownerId: 'user'
                        });

                        setIsDeviceRegistered(false);
                        setHadPtzCapabilities(false);
                    }
                }

            } else {
                // No track or getCapabilities not available
                if (hadPtzCapabilities && isDeviceRegistered && props.socketInstance) {
                    console.log(`PTZ capabilities lost (no track/getCapabilities) for device ${props.stream.id}, sending disconnect`);

                    props.socketInstance.emit('ptz:disconnect', {
                        deviceId: props.stream.id,
                    });

                    setIsDeviceRegistered(false);
                    setHadPtzCapabilities(false);
                }
            }
        }

    }, [props.ptzDeviceInfo, props.streamTrack, props.socketInstance, props.isRemoteStream, props.stream, props.onPtzDeviceRegistered, isDeviceRegistered, hadPtzCapabilities]);

    // Ref para evitar loops infinitos
    const registeredRef = useRef<boolean>(false);

    // Función para manejar comandos PTZ recibidos desde el exterior
    const handlePtzCommandReceived = useCallback((deviceId: string, command: string, value: number) => {
        // Solo procesar comandos para este dispositivo
        if (deviceId === props.streamId) {
            console.log(`Applying PTZ command to local device ${deviceId}: ${command} = ${value}`);

            // Aplicar el comando PTZ localmente si es un stream local
            if (!props.isRemoteStream) {
                const [track] = props.stream.getVideoTracks();
                if (track) {
                    const constraints: any = { advanced: [{}] };
                    constraints.advanced[0][command] = value;

                    track.applyConstraints(constraints).then(() => {
                        console.log(`PTZ ${command} applied successfully: ${value}`);

                        // Actualizar el estado local
                        switch (command) {
                            case 'zoom':
                                setZoom(value);
                                break;
                            case 'pan':
                                setPan(value);
                                break;
                            case 'tilt':
                                setTilt(value);
                                break;
                        }
                    }).catch(error => {
                        console.error(`Error applying PTZ constraint ${command}:`, error);
                    });
                }
            }
        }
    }, [props.streamId, props.isRemoteStream, props.stream]);

    // Registrar el handler globalmente solo para dispositivos PTZ locales
    useEffect(() => {
        // Solo registrar si es un stream local con capacidades PTZ
        if (props.onPtzCommandReceived && props.streamId && !props.isRemoteStream && (zoomStatus || panStatus || tiltStatus) && !registeredRef.current) {
            (window as any).ptzHandlers = (window as any).ptzHandlers || {};
            (window as any).ptzHandlers[props.streamId] = handlePtzCommandReceived;

            console.log(`PTZ handler registered for device ${props.streamId}`);
            registeredRef.current = true;
        }

        // Cleanup: remover el handler cuando el componente se desmonte
        return () => {
            if (props.streamId && (window as any).ptzHandlers) {
                delete (window as any).ptzHandlers[props.streamId];
                registeredRef.current = false;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handlePtzCommandReceived, zoomStatus, panStatus, tiltStatus]);

    // Función para enviar comandos PTZ a dispositivos remotos
    const sendPtzCommand = (action: 'pan' | 'tilt' | 'zoom', value: number) => {
        if (props.isRemoteStream && props.ptzDeviceInfo && props.socketInstance) {
            props.socketInstance.emit('ptz:command:move', {
                deviceId: props.ptzDeviceInfo.id,
                action,
                value
            });
        }
    };

    // Funcion para enviar comandos PTZ a dispositivos locales
    const sendLocalPtzCommand = (action: 'pan' | 'tilt' | 'zoom', value: number) => {
        if (props.socketInstance) {
            props.socketInstance.emit('ptz:command:own-move', {
                deviceId: props.stream.id,
                action,
                value
            });
        }
    }

    // Función para manejar cambios de zoom
    const handleZoomChange = (newZoom: number) => {
        setZoom(newZoom);

        if (props.isRemoteStream && props.ptzDeviceInfo) {
            sendPtzCommand('zoom', newZoom);
        } else {
            // Control local existente
            const [track] = props.stream.getVideoTracks();
            track.applyConstraints({
                // @ts-ignore
                advanced: [{ zoom: newZoom }]
            });

            // Emitir evento de cambio de zoom
            sendLocalPtzCommand('zoom', newZoom);

            // Save complete preset with current pan, tilt and new zoom
            if (props.savePtzPreset) {
                props.savePtzPreset(props.stream.id, {
                    pan: pan,
                    tilt: tilt,
                    zoom: newZoom
                });
            }
        }
    };

    // Función para manejar cambios de pan
    const handlePanChange = (newPan: number) => {
        setPan(newPan);

        if (props.isRemoteStream && props.ptzDeviceInfo) {
            sendPtzCommand('pan', newPan);
        } else {
            // Control local existente
            const [track] = props.stream.getVideoTracks();
            track.applyConstraints({
                // @ts-ignore
                advanced: [{ pan: newPan }]
            });

            // Emitir evento de cambio de pan
            sendLocalPtzCommand('pan', newPan);

            // Save complete preset with new pan and current tilt, zoom
            if (props.savePtzPreset) {
                props.savePtzPreset(props.stream.id, {
                    pan: newPan,
                    tilt: tilt,
                    zoom: zoom
                });
            }
        }
    };

    // Función para manejar cambios de tilt
    const handleTiltChange = (newTilt: number) => {
        setTilt(newTilt);

        if (props.isRemoteStream && props.ptzDeviceInfo) {
            sendPtzCommand('tilt', newTilt);
        } else {
            // Control local existente
            const [track] = props.stream.getVideoTracks();
            track.applyConstraints({
                // @ts-ignore
                advanced: [{ tilt: newTilt }]
            });

            // Emitir evento de cambio de tilt
            sendLocalPtzCommand('tilt', newTilt);

            // Save complete preset with current pan, zoom and new tilt
            if (props.savePtzPreset) {
                props.savePtzPreset(props.stream.id, {
                    pan: pan,
                    tilt: newTilt,
                    zoom: zoom
                });
            }
        }
    };


    // if (!zoomStatus && !panStatus && !tiltStatus) {
    //     onPTZ(false);
    //     return null
    // };

    return !zoomStatus && !panStatus && !tiltStatus ? <>
    {
        props.onPTZ(false)
    }</> : <>
        {
            props.onPTZ(true)
        }

        {
            /* PTZ control button */
            props.renderButton({
                name: 'video.ptz.control',
                title: t('button.video.ptz.control.title').toString(),
                icon: 'las la-sliders-h',
                active: status,
                onClick: () => {
                    setStatus(!status);
                },

            })
        }

        {status && <div className="joystick">
            <i className='las la-times' onClick={(e) => {

                e.preventDefault();
                e.stopPropagation();
                setStatus(false);

            }} />
            {
                zoomStatus && <div>
                    <div className="label">{t('joystick.zoom')}</div>
                    <div className='rang'>
                        <div className='decrease'
                            onClick={() => {
                                // split zoom in steps
                                const step = (maxZoom - minZoom) / 40;
                                const newZoom = zoom - step;
                                handleZoomChange(newZoom);
                            }}
                        >
                            <i className='las la-minus' />
                        </div>
                        <input
                            className="slider"
                            type="range"
                            min={minZoom}
                            max={maxZoom}
                            value={zoom}
                            onChange={(e) => {
                                const newZoom = Number(e.target.value);
                                handleZoomChange(newZoom);
                            }}
                        />
                        <div className='increase'
                            onClick={() => {
                                // split zoom in steps
                                const step = (maxZoom - minZoom) / 40;
                                const newZoom = zoom + step;
                                handleZoomChange(newZoom);
                            }}
                        ><i className='las la-plus' /></div>
                    </div>
                </div>
            }
            {
                panStatus && <div>
                    <div className="label">{t('joystick.pan')}</div>
                    <div className='rang'>
                        <div className='decrease'
                            onClick={() => {
                                // split pan in steps
                                const step = (maxPan - minPan) / 40;
                                const newPan = pan - step;
                                handlePanChange(newPan);
                            }}
                        >
                            <i className='las la-minus'></i>
                        </div>

                        <input className="slider" type="range" min={minPan} max={maxPan} value={pan} onChange={(e) => {
                            const newPan = Number(e.target.value);
                            handlePanChange(newPan);
                        }} />
                        <div className='increase'
                            onClick={() => {
                                // split pan in steps
                                const step = (maxPan - minPan) / 40;
                                const newPan = pan + step;
                                handlePanChange(newPan);
                            }}
                        ><i className='las la-plus'></i></div>
                    </div>
                </div>
            }
            {
                tiltStatus && <div>
                    <div className="label">{t('joystick.tilt')}</div>
                    <div className='rang'>
                        <div className='decrease'
                            onClick={() => {
                                // split tilt in steps
                                const step = (maxTilt - minTilt) / 40;
                                const newTilt = tilt - step;
                                handleTiltChange(newTilt);
                            }}
                        >
                            <i className='las la-minus'></i>
                        </div>

                        <input className="slider" type="range" min={minTilt} max={maxTilt} value={tilt} onChange={(e) => {
                            const newTilt = Number(e.target.value);
                            handleTiltChange(newTilt);
                        }} />
                        <div className='increase'
                            onClick={() => {
                                // split tilt in steps
                                const step = (maxTilt - minTilt) / 40;
                                const newTilt = tilt + step;
                                handleTiltChange(newTilt);
                            }}
                        ><i className='las la-plus'></i></div>
                    </div>
                </div>
            }

        </div>
        }
    </>

};
